import {
  Button,
  Col,
  Input,
  Modal,
  notification,
  Space,
  Spin,
  Table,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import {
  deleteRegion,
  getRegions,
  Region,
  updateRegion,
} from "../../../services/regionsApi";
import { columns } from "./columns";

import "../../../shared/styles/list.css";
import { PlusCircleTwoTone } from "@ant-design/icons";
import ContainerLayout from "../../common/containerLayout";
import { DrawingMap } from "../../map/DrawingMap";
import Wkt from "wicket";
import EditableRegion from "../../../shared/interfaces/EditableRegion";
import { getRandomInt } from "../../../utils/helpers/numberUtils";

const { Text } = Typography;
const { Search } = Input;

const colors = [
  "#b40056",
  "#3b185a",
  "#c02485",
  "#ffde14",
  "#e83278",
  "#009c9b",
  "#c6d76b",
  "#f39200",
  "#f8eb4c",
  "#546918",
  "#ff8379",
];

const RegionList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<Region[]>([]);
  const [search, setSearch] = useState("");
  const [selectedRegionId, setSelectedRegionId] = useState<number | null>(null);
  const [regions, setRegions] = useState<EditableRegion[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [currentPolygon, setCurrentPolygon] =
    useState<google.maps.Polygon | null>(null);
  const [center] = useState<google.maps.LatLngLiteral>({
    lat: -34.88582,
    lng: -56.17122,
  });

  const fetchData = async () => {
    setLoading(true);

    const regions = await getRegions();

    setData(regions);
    buildPolygonsPayload(regions);
    setLoading(false);
  };

  const buildPolygonsPayload = (regions: Region[]) => {
    const paths = [];

    for (const region of regions) {
      try {
        const wkt = new Wkt.Wkt();
        wkt.read(region.polygon);

        const { coordinates } = wkt.toJson();

        const regionCoords = coordinates[0].map((coord) => ({
          lat: coord[1],
          lng: coord[0],
        }));

        const color = colors[getRandomInt(colors.length)];

        const polygon = new google.maps.Polygon({
          paths: regionCoords,
          strokeColor: "#000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.35,
        });

        paths.push({
          id: region.id,
          name: region.name,
          path: regionCoords,
          wkt: wkt.write(),
          polygon,
        });
      } catch (error) {}
    }

    setRegions(paths);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSearch = (value: string) => {
    setSearch(value);
  };

  const filteredData = data.filter((region) =>
    region.name.toLowerCase().includes(search.toLowerCase()),
  );

  const onFinishDrawing = async (
    polygon: google.maps.Polygon,
    regionId?: number,
  ) => {
    const wkt = new Wkt.Wkt();
    wkt.fromObject(polygon);

    if (regionId === undefined || regionId < -1) return;

    const region = regions.find((x) => x.id === regionId);

    if (!region) return;

    await updateRegion(regionId, region.name, wkt.write());
    navigate(0);
  };

  const handleMultiDelete = async () => {
    Modal.confirm({
      title: "¿Seguro que deseas eliminar?",
      content: `Se eliminarán ${selectedRowKeys.length} registros.`,
      okText: "Sí, eliminar",
      cancelText: "Cancelar",
      onOk: async () => {
        try {
          await Promise.all(
            selectedRowKeys.map((regionId) => deleteRegion(Number(regionId))),
          );
          notification.success({
            message: `Las regiones se han eliminado exitosamente`,
            placement: "bottom",
            key: "global",
          });
          navigate(0);
        } catch (e) {
          notification.error({
            message: `Error al eliminar las regiones`,
            placement: "bottom",
            key: "global",
          });
        }
      },
    });
  };

  return (
    <ContainerLayout title="Regiones" showNotification={false}>
      <Col span={24}>
        <Space>
          <Button
            onClick={() =>
              navigate(WebRoutes.CreateRegionRoute, {
                replace: true,
              })
            }
            type="primary"
            icon={<PlusCircleTwoTone />}
          >
            Región
          </Button>
          <Text strong>Buscar</Text>
          <Search
            placeholder="Nombre de la region"
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />
          <Button
            danger
            onClick={handleMultiDelete}
            disabled={selectedRowKeys.length === 0}
          >
            Eliminar seleccionados
          </Button>
        </Space>
      </Col>
      <Col span={8} style={{ width: "100px", height: "83vh" }}>
        <Table
          virtual
          style={{ minWidth: "100%" }}
          columns={columns({ navigate, selectedRegionId, setSelectedRegionId })}
          rowKey={(record) => record.id}
          dataSource={filteredData}
          loading={loading}
          pagination={{
            pageSize: filteredData?.length,
            hideOnSinglePage: true,
          }}
          scroll={{ x: 500, y: 705 }}
          rowSelection={{
            type: "checkbox",
            columnWidth: "10%",
            selectedRowKeys,
            onChange: (newSelectedRowKeys: React.Key[]) =>
              setSelectedRowKeys(newSelectedRowKeys),
          }}
        />
      </Col>
      <Col span={16}>
        <Spin tip="Cargando regiones..." size="large" spinning={loading}>
          <DrawingMap
            zoom={12}
            center={center}
            editableRegions={regions}
            mapContainerStyle={{
              height: "80vh",
              width: "100%",
            }}
            drawable={false}
            selectedRegionId={selectedRegionId!}
            currentPolygon={currentPolygon}
            setCurrentPolygon={setCurrentPolygon}
            onFinishDrawing={onFinishDrawing}
          />
        </Spin>
      </Col>
    </ContainerLayout>
  );
};

export default RegionList;
